import React, { useState } from "react";
import { Container, Row, Col, Card, Modal, ModalBody } from "reactstrap";
import { Play } from "lucide-react";
import { naCardBg, naCardBgHover, NA_PRIMARY } from "./darkSectionTokens";

function youtubeEmbedUrl(youtubeId) {
  return `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1`;
}

function EditionCard({ edition, size }) {
  const [videoOpen, setVideoOpen] = useState(false);
  const hasVideo = Boolean(edition.youtube);

  const openVideo = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setVideoOpen(true);
  };

  return (
    <>
      <Card
        className="edition-card"
        style={{
          background: naCardBg,
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(198, 96, 103, 0.35)",
          borderRadius: "20px",
          padding: 0,
          height: "100%",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          overflow: "hidden",
          position: "relative",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = naCardBgHover;
          e.currentTarget.style.borderColor = "rgba(198, 96, 103, 0.5)";
          e.currentTarget.style.transform = "translateY(-10px) scale(1.02)";
          e.currentTarget.style.boxShadow = "0 20px 60px rgba(198, 96, 103, 0.4)";
          const img = e.currentTarget.querySelector(".edition-thumb");
          if (img) {
            img.style.transform = "scale(1.05)";
          }
          const overlay = e.currentTarget.querySelector(".edition-hover-overlay");
          if (overlay) {
            overlay.style.opacity = hasVideo ? "0.35" : "0.3";
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = naCardBg;
          e.currentTarget.style.borderColor = "rgba(198, 96, 103, 0.35)";
          e.currentTarget.style.transform = "translateY(0) scale(1)";
          e.currentTarget.style.boxShadow = "none";
          const img = e.currentTarget.querySelector(".edition-thumb");
          if (img) {
            img.style.transform = "scale(1)";
          }
          const overlay = e.currentTarget.querySelector(".edition-hover-overlay");
          if (overlay) {
            overlay.style.opacity = "0";
          }
        }}
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            overflow: "hidden",
            borderRadius: "20px 20px 0 0",
          }}
        >
          <img
            src={edition.logo}
            alt={edition.location}
            className="edition-thumb"
            style={{
              width: "100%",
              height: size ? "280px" : "200px",
              objectFit: "cover",
              transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              display: "block",
            }}
          />
          <div
            className="edition-hover-overlay"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background:
                "linear-gradient(135deg, rgba(198, 96, 103, 0.4) 0%, rgba(112, 174, 184, 0.3) 100%)",
              opacity: 0,
              transition: "opacity 0.4s ease",
              pointerEvents: "none",
            }}
          />
          {hasVideo && (
            <button
              type="button"
              onClick={openVideo}
              aria-label={`Play ${edition.location} highlight video`}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: size ? 72 : 60,
                height: size ? 72 : 60,
                borderRadius: "50%",
                border: "2px solid rgba(255, 255, 255, 0.9)",
                background: "rgba(0, 0, 0, 0.55)",
                backdropFilter: "blur(6px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                padding: 0,
                transition: "all 0.3s ease",
                boxShadow: "0 8px 28px rgba(0, 0, 0, 0.45)",
                zIndex: 2,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `${NA_PRIMARY}dd`;
                e.currentTarget.style.transform = "translate(-50%, -50%) scale(1.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(0, 0, 0, 0.55)";
                e.currentTarget.style.transform = "translate(-50%, -50%) scale(1)";
              }}
            >
              <Play
                size={size ? 34 : 28}
                fill="white"
                color="white"
                strokeWidth={1.5}
                style={{ marginLeft: "4px" }}
              />
            </button>
          )}
        </div>

        <div
          style={{
            padding: size ? "1.25rem 1rem" : "1rem",
            textAlign: "center",
          }}
        >
          <a
            href={edition.link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white"
            style={{
              textDecoration: "none",
              fontSize: size ? "1.25rem" : "1.1rem",
              fontWeight: 600,
              lineHeight: 1.5,
              letterSpacing: "0.3px",
            }}
          >
            {edition.location}
            {edition.date && (
              <>
                <br />
                <span
                  style={{
                    fontSize: size ? "1rem" : "0.9rem",
                    fontWeight: 400,
                    color: "#70AEB8",
                  }}
                >
                  {edition.date}
                </span>
              </>
            )}
          </a>
        </div>

        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "50px",
            height: "50px",
            background: "linear-gradient(135deg, rgba(198, 96, 103, 0.1) 0%, transparent 100%)",
            borderRadius: "0 20px 0 100%",
            pointerEvents: "none",
          }}
        />
      </Card>

      {hasVideo && (
        <Modal
          isOpen={videoOpen}
          toggle={() => setVideoOpen(false)}
          size="lg"
          centered
          className="edition-video-modal"
        >
          <ModalBody className="p-0 bg-black" style={{ borderRadius: "8px", overflow: "hidden" }}>
            <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, background: "#000" }}>
              {videoOpen && (
                <iframe
                  title={`${edition.location} highlight`}
                  src={youtubeEmbedUrl(edition.youtube)}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    border: 0,
                  }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              )}
            </div>
          </ModalBody>
        </Modal>
      )}
    </>
  );
}

function Editions() {
  const pageHeader = React.createRef();
  const size =
    document.body.clientWidth >= 1024 || document.documentElement.clientWidth >= 1024;

  return (
    <div
      style={{
        backgroundColor: "transparent",
        padding: 0,
      }}
      className="section"
      data-parallax={true}
      ref={pageHeader}
    >
      <Container style={{ padding: 0 }}>
        <Row style={{ margin: 0 }}>
          <Col lg={12} className="mb-5" style={{ padding: size ? "3rem 15px 2rem" : "2rem 15px 1.5rem" }}>
            <h1
              className="text-center text-g mb-3"
              style={{
                fontSize: size ? "3.5rem" : "2rem",
                fontWeight: "700",
                letterSpacing: "0.5px",
                background: "linear-gradient(135deg, #C66067 0%, #70AEB8 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              GLOBAL EDITIONS
            </h1>
            <div
              style={{
                width: "100px",
                height: "4px",
                background: "linear-gradient(90deg, #C66067, #70AEB8)",
                borderRadius: "2px",
                margin: "0 auto",
              }}
            />
          </Col>
        </Row>
        <Row
          className="justify-content-center"
          style={{ margin: 0, padding: size ? "0 15px 3rem" : "0 15px 2rem" }}
        >
          {events
            .filter((e) => e.youtube)
            .map((e) => (
            <Col lg={6} md={6} xs={12} className="mb-4" key={e.location} style={{ padding: "0 10px" }}>
              <EditionCard edition={e} size={size} />
            </Col>
          ))}
        </Row>
      </Container>
    </div>
  );
}

export default Editions;

const events = [
  {
    logo: require("assets/editions/dubai.jpg"),
    location: "Dubai, UAE",
    date: "",
    link: "https://me.digitaltwin-summit.com",
    youtube: "rah6JCuJKAE",
  },
  {
    logo: require("assets/editions/dubai.jpg"),
    location: "Egypt",
    date: "",
    link: "https://eg.digitaltwin-summit.com",
  },
  {
    logo: require("assets/editions/nz.jpg"),
    location: "Brisbane, Australia",
    date: "",
    link: "https://anz.digitaltwin-summit.com",
    youtube: "J88zdGPyMak",
  },
  {
    logo: require("assets/editions/nz.jpg"),
    location: "Wellington, New Zealand",
    date: "",
    link: "https://anz.digitaltwin-summit.com",
    youtube: "XBmVDytoMWI",
  },
  {
    logo: require("assets/editions/uk.jpg"),
    location: "North America",
    date: "Chicago, USA — 19–20 Aug 2026",
    link: "https://usa.digitaltwin-summit.com",
  },
  {
    logo: require("assets/editions/saudi.jpg"),
    location: "Riyadh, Saudi Arabia",
    date: "",
    link: "https://ksa.digitaltwin-summit.com",
    youtube: "zizuwCozJx0",
  },
  {
    logo: require("assets/editions/uk.jpg"),
    location: "London, United Kingdom",
    date: "",
    link: "https://uk.digitaltwin-summit.com",
  },
];
